import { getSentryDsn } from "@/utils/getSentryDsn";

function getRuntimeEnv(): Record<string, string> | undefined {
  if (globalThis.window === undefined) {
    return undefined;
  }
  return (globalThis.window as { __ENV__?: Record<string, string> }).__ENV__;
}

function hasBuildTimeSupabaseConfig(): boolean {
  return (
    Boolean(import.meta.env.VITE_SUPABASE_URL) && Boolean(import.meta.env.VITE_SUPABASE_ANON_KEY)
  );
}

function isSupabaseConfigReady(): boolean {
  if (hasBuildTimeSupabaseConfig()) {
    return true;
  }
  const runtimeEnv = getRuntimeEnv();
  return Boolean(runtimeEnv?.VITE_SUPABASE_URL && runtimeEnv?.VITE_SUPABASE_ANON_KEY);
}

function isSentryConfigReady(): boolean {
  return Boolean(getSentryDsn());
}

/**
 * Wait for runtime config.js (Supabase + optional Sentry DSN).
 * In production, polls briefly so Cloudflare Rocket Loader cannot race ahead of config.js.
 */
export function waitForRuntimeConfig(): Promise<void> {
  return new Promise((resolve) => {
    const maxWait = import.meta.env.PROD ? 3000 : 1000;
    const checkInterval = 50;
    const startTime = Date.now();

    /**
     * Check.
     */
    const check = () => {
      const supabaseReady = isSupabaseConfigReady();
      const sentryReady = isSentryConfigReady() || !import.meta.env.PROD;

      if (supabaseReady && sentryReady) {
        resolve();
        return;
      }

      if (Date.now() - startTime >= maxWait) {
        if (import.meta.env.PROD && !isSentryConfigReady()) {
          console.warn(
            "⚠️ Sentry DSN not available after config wait; error tracking may be disabled."
          );
        }
        resolve();
        return;
      }

      setTimeout(check, checkInterval);
    };

    check();
  });
}
